/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { TimeComponentTimeAxis } from '../TimeComponent.types';
import { bridgeValues } from '../themes/TimeComponentStyles.css';
import { getTimePosition } from './timeUtils';
import type { useTextDimensions } from '../../hooks/PRIVATE_useTextDimensions';
type GetTextDimensions = NonNullable<ReturnType<typeof useTextDimensions>['getTextDimensions']>;
type Font = NonNullable<Parameters<GetTextDimensions>[1]>;

// TODO: Remove and replace this with the future useTextDimensions batch text measurements solution.
// We also just need width, so maybe we can make useTextDimensions optimize for that case too
// e.g. use canvas backend for width only measurements, etc.
/**
 * Gets the text dimensions of the given array of texts.
 * @param getTextDimensions The measurement function.
 * @param texts An array of texts.
 * @param font The font of the texts.
 * @returns An array of text dimensions.
 */
const getBatchTextDimensions = (
  getTextDimensions: GetTextDimensions,
  texts: string[],
  font?: Font
) => texts.map((text) => getTextDimensions(text, font));

/**
 * Gets the (unresolved) time axis height.
 * @param axisProps The axis props.
 * @returns The (unresolved) height.
 */
const getUnresolvedTimeAxisHeight = (axisProps?: TimeComponentTimeAxis) => {
  return axisProps === undefined
    ? '0px'
    : axisProps.height !== undefined
    ? `${axisProps.height}px`
    : bridgeValues.horizontalTimeAxisHeight;
};

/**
 * Gets the unresolved time axis layout.
 * @param axisPosition The axis position.
 * @param minorAxis The minor axis props.
 * @param majorAxis The major axis props.
 * @returns The unresovled time axis layout.
 */
const getUnresolvedTimeAxesLayout = (
  axisPosition: 'top' | 'bottom',
  minorAxis?: TimeComponentTimeAxis,
  majorAxis?: TimeComponentTimeAxis
) => {
  const minorAxisHeight = getUnresolvedTimeAxisHeight(minorAxis);
  const majorAxisHeight = getUnresolvedTimeAxisHeight(majorAxis);
  const totalHeight = `calc(${majorAxisHeight} + ${minorAxisHeight})`;
  return {
    position: axisPosition,
    height: totalHeight
  };
};

/**
 * Gets the resolved time axis height.
 * @param defaultHeight The default time axis height.
 * @param axisProps The axis props.
 * @returns The time axis height in pixels.
 */
const getResolvedTimeAxisHeight = (defaultHeight: number, axisProps?: TimeComponentTimeAxis) => {
  return axisProps === undefined
    ? 0
    : axisProps.height !== undefined
    ? axisProps.height
    : defaultHeight;
};

/**
 * Gets the resolved time axis props.
 * @param defaultHeight The default time axis height.
 * @param axisProps The axis props.
 * @returns The resolved axis props.
 */
const getResolvedTimeAxisProps = (defaultHeight: number, axisProps: TimeComponentTimeAxis) => {
  const height = getResolvedTimeAxisHeight(defaultHeight, axisProps);
  const { scale, zoomOrder = [axisProps.scale] } = axisProps;
  return {
    height,
    zoomOrder,
    zoomOrderIndex: zoomOrder.indexOf(scale)
  };
};

/**
 * Gets the interval start time relative to the given time according to the given scale.
 * @param scale The time scale.
 * @param time The query time.
 * @returns The interval start time.
 */
const getPreviousTime = (scale: TimeComponentTimeAxis['scale'], time: number) => {
  return Date.parse(scale.getPreviousDate(new Date(time).toISOString()));
};

/**
 * Gets the interval end time relative to the given time according to the given scale.
 * @param scale The time scale.
 * @param time The query time.
 * @returns The interval end time.
 */
const getNextTime = (scale: TimeComponentTimeAxis['scale'], time: number) => {
  return Date.parse(scale.getNextDate(new Date(time).toISOString()));
};

/**
 * Gets the time axis ticks in the given viewport.
 * @param scale The time scale.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @returns The ticks (in time).
 */
const getViewportTicks = (
  scale: TimeComponentTimeAxis['scale'],
  viewportStartTime: number,
  viewportEndTime: number
) => {
  const times = [getPreviousTime(scale, viewportStartTime)];
  while (times[times.length - 1] < viewportEndTime) {
    times.push(getNextTime(scale, times[times.length - 1]));
  }
  return times;
};

/**
 * Returns a set of date intervals sampled using the following procedure:
 * 1. Divide up the time range into numSections sections
 * 2. For each section, take the first numIntervalsPerSection intervals from the start,
 *    plus the last interval of the section.
 * 3. Consolidate all of them and return
 * @param scale
 * @param startTime
 * @param endTime
 * @param numSections
 * @param numIntervalsPerSection
 * @returns The unique intervals retrieved (as a Map of <start time, end time>)
 */
const sampleIntervals = (
  scale: TimeComponentTimeAxis['scale'],
  startTime: number,
  endTime: number,
  numSections: number,
  numIntervalsPerSection: number
) => {
  // key: interval start time, value: interval end time
  // Use Map to prevent duplicate intervals
  const intervals = new Map<number, number>();

  const sectionInterval = Math.floor((endTime - startTime) / numSections);
  for (let i = 0; i < numSections; i++) {
    const sectionStartTime = startTime + sectionInterval * i;
    const sectionEndTime = Math.min(startTime + sectionInterval * (i + 1), endTime);

    let prevTime = getPreviousTime(scale, sectionStartTime);
    for (let j = 0; j < numIntervalsPerSection; j++) {
      if (prevTime >= sectionEndTime) {
        break;
      }
      const currTime = getNextTime(scale, prevTime);
      intervals.set(prevTime, currTime);
      prevTime = currTime;
    }

    // Also consider last label at the end of the section
    prevTime = getPreviousTime(scale, sectionEndTime - 1);
    intervals.set(prevTime, getNextTime(scale, prevTime));
  }

  return intervals;
};

/**
 * Gets (a representative set of) the time intervals in the given time scale.
 * @param scale The time scale.
 * @param samplingStrategy The sampling strategy to use.
 *  The 'exact' type would retrieve ALL intervals in the given range and scale.
 *  'approximate' would retrieve only a representative subset of intervals.
 * @returns (A representative set of) the time intervals in the given time scale.
 */
const getTimeAxisIntervals = (
  scale: TimeComponentTimeAxis['scale'],
  samplingStrategy:
    | {
        type: 'exact';
        params: { startTime: number; endTime: number };
      }
    | {
        type: 'approximate';
        params: {
          numSections: number;
          numIntervalsPerSection: number;
          startTime: number;
          endTime: number;
        };
      }
) => {
  const { type, params } = samplingStrategy;
  switch (type) {
    case 'exact': {
      const intervals = new Map<number, number>();
      const viewportTimes = getViewportTicks(scale, params.startTime, params.endTime);
      for (let i = 0; i < viewportTimes.length - 1; i++) {
        intervals.set(viewportTimes[i], viewportTimes[i + 1]);
      }
      return { intervals, numIntervals: intervals.size };
    }
    case 'approximate': {
      // Rather than computing all dates (and labels) to obtain exact dimensions,
      // heuristically sample a subset of dates for better performance.
      // Assumption is that in many usecases, label widths have small variance.
      // Even if our estimation is off and cause a label trunction, users can manually zoom in to see the full label.
      const { startTime, endTime, numSections, numIntervalsPerSection } = params;
      const intervals = sampleIntervals(
        scale,
        startTime,
        endTime,
        numSections,
        numIntervalsPerSection
      );
      // Estimate real number of intervals
      const intervalStartTimes = [...intervals.keys()];
      const avgTimePerInterval =
        intervalStartTimes.reduce((sum, prevTime) => {
          return sum + (intervals.get(prevTime)! - Number(prevTime));
        }, 0) / intervalStartTimes.length;
      const estNumIntervals = (endTime - startTime) / avgTimePerInterval;
      return { intervals, numIntervals: estNumIntervals };
    }
    default: {
      const _exhaustiveCheck: never = type;
      return _exhaustiveCheck;
    }
  }
};

/**
 * Gets the zoom order characteristics based on the given time intervals, and widths of those intervals.
 * @param getTextDimensions The text measurement function.
 * @param intervalPaddingStart The interval padding start.
 * @param intervalPaddingEnd The interval padding end.
 * @param minIntervalWidth The minimum interval width.
 * @param axisFont The font.
 * @param zoomOrder The zoom order.
 * @param samplingStrategy The sampling strategy to use to retrieve intervals.
 * @returns The min length factor for each scale in the zoom order, and (an estimate of) the max number of intervals in the time axis across all scales in the zoom order.
 */
const getZoomOrderCharacteristics = (
  getTextDimensions: GetTextDimensions,
  intervalPaddingStart: number,
  intervalPaddingEnd: number,
  minIntervalWidth: number,
  axisFont: Font,
  zoomOrder: NonNullable<TimeComponentTimeAxis['zoomOrder']>,
  samplingStrategy: Parameters<typeof getTimeAxisIntervals>[1]
) => {
  const intervalPadding = intervalPaddingStart + intervalPaddingEnd;
  const zoomLevelIntervals = zoomOrder.map((scale) =>
    getTimeAxisIntervals(scale, samplingStrategy)
  );
  const maxNumIntervals = Math.max(...zoomLevelIntervals.map(({ numIntervals }) => numIntervals));
  const zoomOrderMinLengthFactor = zoomLevelIntervals.map(({ intervals }, i) => {
    const scale = zoomOrder[i];
    const prevTimes = [...intervals.keys()];
    const labels = prevTimes.map((prevTime) => getTimeLabel(scale, intervals.get(prevTime)!));
    const intervalDurations = prevTimes.map((prevTime) => intervals.get(prevTime)! - prevTime);
    const intervalLabelDimensions = getBatchTextDimensions(getTextDimensions, labels, axisFont);
    const minLengthFactor = Math.min(
      ...intervalDurations.map((duration, i) => {
        const contentWidth = Math.max(
          minIntervalWidth,
          intervalLabelDimensions[i].width + intervalPadding
        );
        return duration / contentWidth;
      })
    );
    return minLengthFactor;
  });

  return {
    zoomOrderMinLengthFactor,
    maxNumIntervals
  };
};

/**
 * Gets the resolved viewport range given the range constraints.
 * @param viewportDuration The viewport duration.
 * @param startTime The start time.
 * @param endTime The end time.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @returns The resolved viewport range.
 */
const getResolvedViewport = (
  viewportDuration: number,
  startTime: number,
  endTime: number,
  viewportStartTime?: number,
  viewportEndTime?: number
) => {
  if (viewportStartTime !== undefined && viewportEndTime !== undefined) {
    return { viewportStartTime, viewportEndTime };
  }
  if (viewportStartTime !== undefined && viewportEndTime === undefined) {
    return {
      viewportStartTime,
      viewportEndTime: Math.min(endTime, viewportStartTime + viewportDuration)
    };
  }
  if (viewportStartTime === undefined && viewportEndTime !== undefined) {
    return {
      viewportStartTime: Math.max(startTime, viewportEndTime - viewportDuration),
      viewportEndTime
    };
  }
  return {
    viewportStartTime: startTime,
    viewportEndTime: Math.min(endTime, startTime + viewportDuration)
  };
};

/**
 * Gets the formatted string for the given time in the given scale.
 * This is used for the time axis interval labels.
 * @param scale The time scale.
 * @param time The time.
 * @returns The formatted string.
 */
const getTimeLabel = (scale: TimeComponentTimeAxis['scale'], time: number) =>
  scale.formatter(new Date(time).toISOString());

/**
 * Gets the viewport axis ticks and labels for the given scale.
 * @param scale The time scale.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @returns The ticks and labels.
 */
const getViewportLabelsTicks = (
  scale: TimeComponentTimeAxis['scale'],
  viewportStartTime: number,
  viewportEndTime: number
) => {
  // ticks.length >= 2 always, because there's always the first and last tick.
  const ticks = getViewportTicks(scale, viewportStartTime, viewportEndTime);

  // We don't need the label of the last tick.
  const labels = ticks.slice(0, ticks.length - 1).map((t) => getTimeLabel(scale, t));

  return { ticks, labels };
};

/**
 * Gets the time axis layout.
 * @param getTextDimensions The text measurement function.
 * @param labelPaddingStart The interval label padding start.
 * @param labelPaddingEnd The interval label padding end.
 * @param font The font.
 * @param labelPosition The label position.
 * @param ticks The ticks (in time).
 * @param gridTicks The subset of visible ticks that mark the vertical tick lines (in time).
 * @param labels The interval labels.
 * @param startTime The start time.
 * @param endTime The end time.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param width The viewport width.
 * @param isRTL Whether the reading direction is rtl.
 * @returns The time axis layout.
 */
const getTimeAxisLayout = (
  getTextDimensions: GetTextDimensions,
  labelPaddingStart: number,
  labelPaddingEnd: number,
  font: Font,
  labelPosition: TimeComponentTimeAxis['scale']['labelPosition'] = 'start',
  ticks: number[],
  gridTicks: number[],
  labels: string[],
  startTime: number,
  endTime: number,
  viewportStartTime: number,
  viewportEndTime: number,
  width: number,
  isRTL: boolean
) => {
  const intervalPadding = labelPaddingStart + labelPaddingEnd;
  const vs = viewportStartTime;
  const ve = viewportEndTime;
  const labelTicks = ticks.slice(0, ticks.length - 1);
  const gridTicksPos = gridTicks.map((t) => getTimePosition(t, vs, ve, width, isRTL));
  const ticksPos = ticks.map((t) => getTimePosition(t, vs, ve, width, isRTL));
  const intervalWidths = ticksPos
    .slice(1)
    .map((p, i) => (isRTL ? ticksPos[i] - p : p - ticksPos[i]));
  // Labels that start before the startTime or ends after the endTime need to be truncated
  const isIntervalStartTruncated = ticks[0] < startTime;
  if (isIntervalStartTruncated) {
    ticksPos[0] = 0;
    intervalWidths[0] = ticksPos[1];
  }
  if (ticks[ticks.length - 1] > endTime) {
    ticksPos[ticksPos.length - 1] = isRTL ? 0 : width;
    intervalWidths[intervalWidths.length - 1] = isRTL
      ? ticksPos[ticksPos.length - 2] - ticksPos[ticksPos.length - 1]
      : ticksPos[ticksPos.length - 1] - ticksPos[ticksPos.length - 2];
  }
  const labelPositions = ticksPos
    .slice(0, ticksPos.length - 1)
    .map((p, i) => (isRTL ? p - intervalWidths[i] : p));
  const isLabelTruncated = getBatchTextDimensions(getTextDimensions, labels, font).map(
    ({ width }, i) => Math.floor(width) - Math.floor(intervalWidths[i] - intervalPadding) > 0
  );

  return {
    font,
    labelPosition,
    labels,
    labelTicks,
    labelPositions,
    isLabelTruncated,
    isIntervalStartTruncated,
    intervalWidths,
    gridTicksPos,
    isRTL
  };
};

/**
 * Gets the time axes layout for the viewport.
 * @param getTextDimensions The text measurement function.
 * @param labelPaddingStart The interval label padding start.
 * @param labelPaddingEnd The interval label padding end.
 * @param majorAxisFont The major axis font.
 * @param minorAxisFont The minor axis font.
 * @param startTime The start time.
 * @param endTime The end time.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param width The viewport width.
 * @param isRTL Whether the reading direction is rtl.
 * @param minorAxisScale The minor axis scale.
 * @param majorAxisScale The major axis scale.
 * @returns The time axes layout for the viewport.
 */
const getTimeAxesViewportLayout = (
  getTextDimensions: GetTextDimensions,
  labelPaddingStart: number,
  labelPaddingEnd: number,
  majorAxisFont: Font,
  minorAxisFont: Font,
  startTime: number,
  endTime: number,
  viewportStartTime: number,
  viewportEndTime: number,
  width: number,
  isRTL: boolean,
  minorAxisScale: TimeComponentTimeAxis['scale'],
  majorAxisScale?: TimeComponentTimeAxis['scale']
) => {
  const majorAxisLabelsTicks = majorAxisScale
    ? getViewportLabelsTicks(majorAxisScale, viewportStartTime, viewportEndTime)
    : { ticks: [], labels: [] };

  const minorAxisLabelsTicks = getViewportLabelsTicks(
    minorAxisScale,
    viewportStartTime,
    viewportEndTime
  );

  // Determine whether to render secondary major gridlines
  // If major dates is a subset of minor dates, then the two grids line up.
  // Don't render the minor dates at overlap
  // (due to opacity, minor line would show through major line otherwise).
  // Otherwise the major and minor axis do not "line up"
  // (e.g. months and weeks scale together). Render minor lines only.
  const { ticks: majorTicks, labels: majorLabels } = majorAxisLabelsTicks;
  const { ticks: minorTicks, labels: minorLabels } = minorAxisLabelsTicks;
  const majorTicksSet = new Set(majorTicks);
  const minorTicksSet = new Set(minorTicks);
  // only consider major dates that are in range (e.g. first and/or last ticks may be out of range)
  const showMajorTicks =
    majorTicks.filter((t) => t > viewportStartTime && t < viewportEndTime && !minorTicksSet.has(t))
      .length === 0;
  const minorGridTicks = showMajorTicks
    ? minorTicks.filter((t) => !majorTicksSet.has(t))
    : minorTicks;

  const majorAxisLayout =
    majorAxisScale &&
    getTimeAxisLayout(
      getTextDimensions,
      labelPaddingStart,
      labelPaddingEnd,
      majorAxisFont,
      majorAxisScale.labelPosition,
      majorTicks,
      majorTicks,
      majorLabels,
      startTime,
      endTime,
      viewportStartTime,
      viewportEndTime,
      width,
      isRTL
    );

  const minorAxisLayout = getTimeAxisLayout(
    getTextDimensions,
    labelPaddingStart,
    labelPaddingEnd,
    minorAxisFont,
    minorAxisScale.labelPosition,
    minorTicks,
    minorGridTicks,
    minorLabels,
    startTime,
    endTime,
    viewportStartTime,
    viewportEndTime,
    width,
    isRTL
  );

  return {
    minorAxisLayout: {
      majorGridTicksPos:
        showMajorTicks && majorAxisLayout ? majorAxisLayout.gridTicksPos : undefined,
      minorGridTicksPos: minorAxisLayout.gridTicksPos,
      variant: 'minor' as const,
      ...minorAxisLayout
    },
    majorAxisLayout: majorAxisLayout && {
      majorGridTicksPos: majorAxisLayout.gridTicksPos,
      minorGridTicksPos: undefined,
      variant: 'major' as const,
      ...majorAxisLayout
    }
  };
};

/**
 * Gets the time axes zoom parameters.
 * @param getTextDimensions The text measurement function.
 * @param labelPaddingStart The interval label padding start.
 * @param labelPaddingEnd The interval label padding end.
 * @param labelMinWidth The interval label min width.
 * @param minorAxisFont The minor axis font.
 * @param startTime The start time.
 * @param endTime The end time.
 * @param minorAxisZoomOrder The minor axis zoom order.
 * @returns The time axes zoom parameters.
 */
const getTimeAxesZoomParameters = (
  getTextDimensions: GetTextDimensions,
  labelPaddingStart: number,
  labelPaddingEnd: number,
  labelMinWidth: number,
  minorAxisFont: Font,
  startTime: number,
  endTime: number,
  minorAxisZoomOrder: NonNullable<TimeComponentTimeAxis['zoomOrder']>
) => {
  const { zoomOrderMinLengthFactor, maxNumIntervals } = getZoomOrderCharacteristics(
    getTextDimensions,
    labelPaddingStart,
    labelPaddingEnd,
    labelMinWidth,
    minorAxisFont,
    minorAxisZoomOrder,
    // 4 sections and 10 interval sections is arbitrary, which samples some labels at every quarter.
    // This is more than enough for the current default scales and labels because they're very equally spaced.
    // but this may not work well with weird custom scales such as reptitions of e.g. [3 weeks, 7 days, 2 months]
    // for a span of 1 year, in which case we may only see weeks and months in our sample, but not days,
    // and we underestimate the content lengths.
    // But even then, users may still be able to zoom in sufficiently to see everything they need.
    // If it turns out this doesn't work well for some common cases, we can bump these params up
    // to cover more intervals in the future.
    {
      type: 'approximate',
      params: { numSections: 4, numIntervalsPerSection: 10, startTime, endTime }
    }
  );

  const getZoomOrderViewDurationThresholds = (width: number) =>
    zoomOrderMinLengthFactor.map((l) => width * l);
  const minZoomViewportDuration = (endTime - startTime) / maxNumIntervals;
  const maxZoomViewportDuration = endTime - startTime;

  return {
    getZoomOrderViewDurationThresholds,
    minorAxisZoomOrder,
    minZoomViewportDuration,
    maxZoomViewportDuration,
    startTime,
    endTime
  };
};

/**
 * Returns a function that given the viewport width, returns the resolved time axes layout for the given viewport.
 * @param getTextDimensions The text measurement function.
 * @param labelPaddingStart The interval label padding start.
 * @param labelPaddingEnd The interval label padding end.
 * @param majorAxisFont The major axis font.
 * @param minorAxisFont The minor axis font.
 * @param getZoomOrderViewDurationThresholds A function that given the viewport width, returns the zoom order viewport duration thresholds.
 * @param axisPosition The axis position.
 * @param startTime The start time.
 * @param endTime The end time.
 * @param isRTL Whether the reading direction is rtl.
 * @param resolvedMinorAxisProps The resolved minor axis props.
 * @param resolvedMajorAxisProps The resolved major axis props.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @returns A function that given the viewport width, returns the resolved time axes layout for the given viewport.
 */
const getResolvedTimeAxesLayout = (
  getTextDimensions: GetTextDimensions,
  labelPaddingStart: number,
  labelPaddingEnd: number,
  majorAxisFont: Font,
  minorAxisFont: Font,
  getZoomOrderViewDurationThresholds: (width: number) => number[],
  axisPosition: 'top' | 'bottom',
  startTime: number,
  endTime: number,
  isRTL: boolean,
  resolvedMinorAxisProps: ReturnType<typeof getResolvedTimeAxisProps>,
  resolvedMajorAxisProps?: ReturnType<typeof getResolvedTimeAxisProps>,
  viewportStartTime?: number,
  viewportEndTime?: number
) => {
  return (width: number) => {
    const { zoomOrder, zoomOrderIndex } = resolvedMinorAxisProps;
    const zoomOrderViewDurationThresholds = getZoomOrderViewDurationThresholds(width);

    const {
      viewportStartTime: resolvedViewportStartTime,
      viewportEndTime: resolvedViewportEndTime
    } = getResolvedViewport(
      zoomOrderViewDurationThresholds[zoomOrderIndex],
      startTime,
      endTime,
      viewportStartTime,
      viewportEndTime
    );

    const { majorAxisLayout, minorAxisLayout } = getTimeAxesViewportLayout(
      getTextDimensions,
      labelPaddingStart,
      labelPaddingEnd,
      majorAxisFont,
      minorAxisFont,
      startTime,
      endTime,
      resolvedViewportStartTime,
      resolvedViewportEndTime,
      width,
      isRTL,
      zoomOrder[zoomOrderIndex],
      resolvedMajorAxisProps &&
        resolvedMajorAxisProps.zoomOrder[resolvedMajorAxisProps.zoomOrderIndex]
    );

    return {
      position: axisPosition,
      startTime,
      endTime,
      viewportStartTime: resolvedViewportStartTime,
      viewportEndTime: resolvedViewportEndTime,
      width,
      majorAxis: majorAxisLayout && { width, ...resolvedMajorAxisProps!, ...majorAxisLayout },
      minorAxis: { width, ...resolvedMinorAxisProps, ...minorAxisLayout }
    };
  };
};

export {
  getResolvedTimeAxisProps,
  getUnresolvedTimeAxesLayout,
  getResolvedTimeAxesLayout,
  getTimeAxesZoomParameters
};
